/**
 * The row codec the sheet itself uses: a wire `RawRow` to its `Descriptor`
 * (`descriptorOf`, through the per-type builder table), its local id
 * (`rowLocalIdOf`) and its reconciled metadata (`reconcileRowMetadata`); a
 * live row back to its wire `RawRow` (`snapshotRow`, read off the stores a
 * `RowSnapshotSource` hands in, never off a `Sheet`); and the id re-key a
 * fork or a promote applies to a snapshot (`remapRowIds`).
 *
 * It sits below `Sheet`: `Sheet.addRow`, `detachAlias` and the copy-on-write
 * paths call it, and so does the hydrator above the sheet
 * (`../raw/raw-sheet.ts`, whose `RawSheet` namespace keeps `toDescriptor`,
 * `toLocalId`, `snapshotRow`, `remapRowIds`, `cellCardinality`,
 * `unknownDescriptorTypes` and `descriptorBuilderTypes` as the published
 * names of these functions).
 */

import type { Option } from "../core/option.ts";
import { Model, Scalar } from "@tento-core/model";
import { Tracing } from "@tento-core/tracing";
import {
  Alias,
  ALL as ROW_TYPE_CATALOG,
  contains as isCatalogRowType,
  Inert,
  Layout,
  Source,
} from "../@generated/row-type-catalog.ts";
import type {
  AliasDescriptor,
  Descriptor,
  InertDescriptor,
  LayoutDescriptor,
  RendererDescriptor,
  RetiredRendererType,
  SourceDescriptor,
} from "../rows/descriptor.ts";
import type { CellCardinality, Dtype, SourceShape } from "../core/dtype.ts";
import { type JsonObject, jsonObjectOf } from "../core/json.ts";
import { isCanonicalKey } from "../backends/canonical-backend.ts";
import { rendererIdOf } from "../rows/slot.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { ModelRowReads } from "../rows/model-row-reads.ts";
import { liveRowOf } from "../rows/model-row.ts";
import { RowTypeSpecOps } from "../rows/row-type-spec.ts";
import { CalendarSystemRow } from "../rows/variants/calendar-system-row.ts";
import { FormulaDataSource } from "../rows/variants/formula-data-source.ts";
import { ListDataSource } from "../rows/variants/list-data-source.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import type {
  AliasInstanceStore,
  RenderlessStore,
  RowMetadata,
  RowMetadataStore,
  SourceRowCellData,
  TreeStructure,
} from "../stores/index.ts";
import type { SheetRegistries } from "./sheet-registries.ts";
import type { RawCell, RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";

/**
 * What `snapshotRow` reads a row off: the sheet's row metadata, tree,
 * alias-instance, renderless and source-cell stores, and its canonical cell
 * store (where a canonical-keyed row's cells live).
 */
export interface RowSnapshotSource {
  readonly rowMetadata: RowMetadataStore;
  readonly tree: TreeStructure;
  readonly aliasInstance: AliasInstanceStore;
  readonly renderless: RenderlessStore;
  readonly sourceCellData: SourceRowCellData;
  readonly canonicalCells: SourceRow.CellDataStore<string>;
}

/**
 * Decode the cell identity shape without constructing renderers or aliases.
 * Transport metadata can carry a source shape without authored alias props.
 * The caller supplies the existing semantic default for its row descriptor.
 */
export function cellCardinalityOf(row: RawRow, fallback: CellCardinality): CellCardinality {
  return decodedAttr(row, "cellCardinality", CELL_CARDINALITY) ?? fallback;
}

/**
 * Pure. Raw row → `Descriptor`, resolved through `registries` (a `data`
 * row is a chart alias when its lookup key has a data preset there).
 * Unknown wire types warn once and fall back to `text` so stale fixtures
 * or persisted rows do not abort hydrate.
 */
export function descriptorOf(row: RawRow, registries: SheetRegistries): Descriptor {
  const builder = DESCRIPTOR_BUILDERS.get(row.type);
  if (builder) return builder(row, registries);
  warnUnknownRowType(row.type);
  return source(row, { dtype: "text" });
}

/**
 * Return legacy wire row types that would hit the warn-fallback in
 * `descriptorOf`. This keeps fixture and import audits out of the
 * warning side effect, so tests can fail directly on missing
 * descriptor coverage.
 */
export function unknownDescriptorTypes(sheet: RawSheet): readonly string[] {
  const types = new Set<string>();
  for (const row of sheet.rows) {
    if (!DESCRIPTOR_BUILDERS.has(row.type)) types.add(row.type);
  }
  return [...types].sort();
}

/**
 * Every wire type with a registered descriptor builder. Lets parity
 * audits assert the reverse direction (no builder key is a qualified
 * renderer id outside the canonical catalog) without exposing the map.
 */
export function descriptorBuilderTypes(): readonly string[] {
  return [...DESCRIPTOR_BUILDERS.keys()];
}

/**
 * Pure (given registry). Intern a `RowLocalId` for a raw row.
 * Repeated calls with the same registry + raw id return the same
 * instance (reference equality).
 */
export function rowLocalIdOf(
  row: RawRow,
  registry: RowLocalId.Registry,
): RowLocalId {
  return registry.resolveFromRawId(row.id);
}

/**
 * Rewrite a `RawRow`'s own id, its `parentId`, and the
 * `attributes.parentRowId` back-reference (carried by `storage:
 * "parent"` rows such as renderers) through `idRemap`. Ids outside
 * the remap pass through unchanged; every other field is preserved.
 * Shared by `Sheet.detachAlias` (virtual→real materialization) and
 * `SheetModelCache.rekeyRows` (ephemeral→server promote re-key).
 */
export function remapRowIds(
  raw: RawRow,
  idRemap: ReadonlyMap<string, string>,
): RawRow {
  const remap = (s: string): string => idRemap.get(s) ?? s;
  const parentId = raw.parentId != null ? RowNetworkKey.fromWire(remap(raw.parentId)) : undefined;
  const attrs = raw.attributes;
  const parentRowId =
    attrs != null && typeof attrs.parentRowId === "string"
      ? remap(attrs.parentRowId)
      : undefined;
  const attributes =
    parentRowId !== undefined ? { ...attrs, parentRowId } : attrs;
  return {
    ...raw,
    id: RowNetworkKey.fromWire(remap(raw.id)),
    ...(parentId !== undefined ? { parentId } : {}),
    ...(attributes !== undefined ? { attributes } : {}),
  };
}

/**
 * A live row as its wire `RawRow`: metadata, parent, attributes (with an
 * alias's live instance, a renderless mark and a data renderer's id merged
 * in) and its seed cells, read off `stores`. Does not skip synthetic rows;
 * the caller decides what to snapshot (`RawSheet.serialize` skips them).
 */
export function snapshotRow(stores: RowSnapshotSource, row: ModelRow): RawRow {
  const meta = stores.rowMetadata.get(row.id);
  // The client does NOT preserve the legacy wire `type` string.
  // `typeForRow` gives a best-effort descriptor → type mapping
  // (obj rows all collapse to `"data-source"`); the server is
  // responsible for bridging into its canonical classification
  // (data / compute / renderer / layout / alias) on ingest.
  const type = typeForRow(row);
  const parent = stores.tree.parent(row.id);

  // For alias rows, the live instance state (props / overrides /
  // hydrated) lives in `aliasInstance`, not in `metadata.attributes`.
  // Merge the instance snapshot over the attribute bag so
  // serialize → hydrate round-trips patchAliasProps / detach /
  // reset mutations faithfully.
  const attributes = mergeRendererIdIntoAttributes(
    row,
    mergeRenderlessIntoAttributes(
      stores,
      row,
      mergeAliasInstanceIntoAttributes(stores, row, meta.attributes ?? null),
    ),
  );

  const raw: RawRow = {
    id: RowNetworkKey.fromWire(row.id.rawId),
    type,
    ...(meta.label != null ? { label: meta.label } : {}),
    ...(meta.formula != null ? { formula: meta.formula } : {}),
    ...(meta.lookupKey != null ? { lookupKey: meta.lookupKey } : {}),
    ...(parent != null ? { parentId: RowNetworkKey.fromWire(parent.rawId) } : {}),
    ...(attributes && Object.keys(attributes).length > 0
      ? { attributes }
      : {}),
    ...(meta.meta && Object.keys(meta.meta).length > 0
      ? { meta: meta.meta }
      : {}),
    ...serializedCells(stores, row),
  };
  return raw;
}

/**
 * For alias rows, replace the hydrate-time `attributes` bag with
 * the live `aliasInstance` snapshot so mutations through
 * `patchAliasProps` / `detach` / `reset` round-trip through
 * serialize → hydrate. Non-alias rows pass through unchanged.
 */
function mergeAliasInstanceIntoAttributes(
  stores: RowSnapshotSource,
  row: ModelRow,
  attributes: Option<JsonObject>,
): Option<JsonObject> {
  if (row.descriptor.role !== "alias") return attributes;
  const instance = stores.aliasInstance.get(row.id);
  if (!instance.template) return attributes;
  return {
    ...(attributes ?? {}),
    template: instance.template,
    // The instance store holds props/overrides untyped; the wire bag
    // carries their JSON form.
    props: jsonObjectOf(instance.props) ?? {},
    overrides: jsonObjectOf(instance.overrides) ?? {},
    hydrated: instance.hydrated,
  };
}

/**
 * For a render-less source (copy-on-write: its default renderer was
 * materialized into a real renderer child), stamp `renderless: true` onto the
 * attribute bag so `RawSheet.hydrate`'s synthesis gate skips re-creating a
 * virtual renderer on re-hydrate. Other rows pass through unchanged. Inert
 * until the materialize-on-edit path marks a source render-less.
 */
function mergeRenderlessIntoAttributes(
  stores: RowSnapshotSource,
  row: ModelRow,
  attributes: Option<JsonObject>,
): Option<JsonObject> {
  if (!stores.renderless.isRenderless(row.id)) return attributes;
  return { ...(attributes ?? {}), renderless: true };
}

/**
 * Round-trip fidelity net for NON-catalog rendererIds only. A catalog
 * renderer now carries its qualified id as the emitted `_type` (see
 * `typeForRow` role "renderer"), so its `data-renderer` builder no longer runs
 * on re-hydrate and the stamp is redundant. For a non-catalog rendererId
 * (unsupported-legacy / `rendererOverride` marks whose surface depends on
 * parent layout) the row still emits generic `data-renderer`, so stamp its
 * `rendererId` onto the attribute bag so `toDescriptor("data-renderer")` can
 * reconstruct it on re-hydrate. Needed once a default renderer is materialized
 * into a real persisted row (copy-on-write).
 */
function mergeRendererIdIntoAttributes(
  row: ModelRow,
  attributes: Option<JsonObject>,
): Option<JsonObject> {
  if (row.descriptor.role !== "renderer") return attributes;
  if (isCatalogRowType(row.descriptor.rendererId)) return attributes;
  return { ...(attributes ?? {}), rendererId: row.descriptor.rendererId };
}

/**
 * Emit `cells[]` for source rows that aren't formula-derived;
 * formula cells are recomputed on re-hydrate so persisting them
 * would lie about the source of truth.
 */
function serializedCells(
  stores: RowSnapshotSource,
  row: ModelRow,
): { cells?: readonly RawCell[] } {
  if (row.descriptor.role !== "source") return {};
  if (row instanceof SourceRow && row.cellsDerived) return {};
  // A canonical-keyed row's cells were routed into `canonicalCells` (keyed by
  // lookupKey) at hydrate, NOT `sourceCellData` (keyed by local id) — see the
  // seed split in `RawSheet.hydrate`. Read from whichever store actually holds
  // this row's cells so serialize round-trips BOTH; otherwise a promote's
  // serialize→hydrate silently drops canonical seed data (e.g. `:~lona:*`
  // rows), which is exactly the "seeded data lost on claim" bug.
  const lookupKey = stores.rowMetadata.get(row.id).lookupKey;
  const canonicalKey = isCanonicalKey(lookupKey) ? lookupKey : null;
  const entries = canonicalKey !== null
    ? stores.canonicalCells.cells(canonicalKey, null)
    : stores.sourceCellData.cells(row.id, null);
  if (entries.length === 0) return {};
  const cells: RawCell[] = [];
  for (const e of entries) {
    cells.push({
      date: e.date.toString(),
      value: e.value,
      ...(e.id != null ? { cellId: e.id } : {}),
    });
  }
  return { cells };
}

/**
 * Wire-format `type` resolver for a `ModelRow`. Derives the
 * type string from the row's descriptor (role + dtype +
 * sourceShape + layout / renderer slot). Used by `serializeRow`
 * to round-trip `Sheet` → `RawSheet` and exported as
 * `wireTypeOf` for consumers that need a default
 * `wireTypeOf` for `SheetViewModel.Deps`.
 *
 * Obj-dtype source rows collapse to the generic `"data-source"` —
 * the descriptor alone can't distinguish weather / tasks /
 * etc. Consumers that need the precise wire type for those
 * should plumb their own resolver (e.g. via metadata).
 */
export function wireTypeOf(row: ModelRowReads): string {
  return typeForRow(row);
}

function typeForRow(read: ModelRowReads): string {
  // A sheet's reads hand out row faces: the class checks below read the live row.
  const row = liveRowOf(read);
  const d = row.descriptor;
  switch (d.role) {
    case "source": {
      if (row instanceof FormulaDataSource) return "formula";
      if (row instanceof CalendarSystemRow) return "calendar-system";
      if (row instanceof ListDataSource) return "list";
      switch (d.dtype) {
        case "text":
          return "text";
        case "number":
          if (d.sourceShape === "per-cell") return "graph";
          if (d.sourceShape === "series") return "series-summary";
          if (d.sourceShape === "tensor") return "heatmap";
          return "number";
        case "boolean":
          return "checkmark";
        case "obj":
          return d.objSubtype ?? "data-source";
      }
      break;
    }
    case "layout": {
      switch (d.layout) {
        case "column": return "column";
        case "canvas": return "canvas";
        case "timeline[row]": return "timeline[row]";
        case "timeline[column]": return "timeline[column]";
        case "grid": return "grid";
        case "calendar-body": return "calendar-body";
      }
      break;
    }
    case "inert":
      return d.kind;
    case "renderer":
      // Self-describing wire type: a catalog renderer emits its qualified
      // registry id (`canvas.points`, `column.timezone`, …) which round-trips
      // via the loop-registered identity builder. Non-catalog rendererIds
      // (unsupported-legacy / rendererOverride marks whose surface depends on
      // parent layout) have no catalog entry, so they stay generic
      // `data-renderer` + `attributes.rendererId` (see
      // `mergeRendererIdIntoAttributes`).
      return isCatalogRowType(d.rendererId) ? d.rendererId : "data-renderer";
    case "alias":
      // Self-describing wire type: a catalog alias emits its concrete
      // template (`alias.weather`, …) which round-trips via the direct
      // parser builder. Non-catalog templates (`data`, `alias.chart`,
      // `alias.default-weather`) have no direct builder, so they stay
      // generic `alias` + `attributes.template`.
      return d.template !== Alias.ALIAS && isCatalogRowType(d.template)
        ? d.template
        : Alias.ALIAS;
  }
  throw new Error(
    `typeForRow: no wire-format type mapping for ${row.constructor.name}`,
  );
}

// ─── Descriptor builders (internal) ────────────────────────────────

function source(
  row: RawRow,
  base: Pick<SourceDescriptor, "dtype"> &
    Partial<
      Pick<
        SourceDescriptor,
        | "sourceShape"
        | "cellCardinality"
        | "formulaSource"
        | "rendererOverride"
      >
    >,
): SourceDescriptor {
  return {
    role: "source",
    dtype: base.dtype,
    sourceShape: base.sourceShape ?? "scalar",
    cellCardinality: base.cellCardinality ?? "single",
    // Preserve the precise obj subtype (weather/tasks/list/…) so
    // `wireTypeOf` doesn't collapse it to the generic "data-source".
    ...(base.dtype === "obj" ? { objSubtype: row.type } : {}),
    ...(base.formulaSource !== undefined
      ? { formulaSource: base.formulaSource }
      : {}),
    ...(base.rendererOverride !== undefined
      ? { rendererOverride: base.rendererOverride }
      : {}),
  };
}

function layout(
  _row: RawRow,
  kind: LayoutDescriptor["layout"],
): LayoutDescriptor {
  return { role: "layout", layout: kind };
}

function inert(
  _row: RawRow,
  kind: string,
): InertDescriptor {
  return { role: "inert", kind };
}

function attr(row: RawRow, key: string): unknown {
  return row.attributes?.[key];
}

/** The row-shape attributes a source row's descriptor reads, as decoders. */
const DTYPE: Model<Dtype> = Scalar.stringUnion<Dtype>(["text", "number", "boolean", "obj"]);
const CARDINALITY: Model<SourceShape> = Scalar.stringUnion<SourceShape>([
  "scalar",
  "series",
  "tensor",
  "per-cell",
]);
const CELL_CARDINALITY: Model<CellCardinality> = Scalar.stringUnion<CellCardinality>([
  "single",
  "multi",
]);

/**
 * Row attribute `key` decoded through `model`: an absent (or `null`) key is
 * `null`, and so is a value `model` refuses — which is reported as a decode
 * issue (`raw-sheet/attribute-decode`, with the row, the key and the issue),
 * so a descriptor's default never silently stands in for an unknown value.
 */
function decodedAttr<T>(row: RawRow, key: string, model: Model<T>): Option<T> {
  const raw = row.attributes?.[key];
  if (raw === undefined || raw === null) return null;
  const result = model.safeHydrate(raw);
  if (result.isOk) return result.value;
  Tracing.log("raw-sheet/attribute-decode", {
    rowId: row.id,
    rowType: row.type,
    key,
    issues: result.issues.map((issue) => issue.message).join("; "),
  });
  return null;
}

/** `{ formulaSource }` when the row names one; nothing otherwise. */
function optionalFormulaSource(formulaSource: Option<string>): { formulaSource?: string } {
  return formulaSource != null ? { formulaSource } : {};
}

/**
 * Build a row's `meta` field by merging row-type metadata defaults
 * (from `ROW_TYPE_SPECS`) with the row's wire `meta`. Instance meta
 * wins. Used by both `RawSheet.hydrate` and `Sheet.addRow` so the two
 * paths produce the same observable metadata post-construction. The
 * wire `type` is no longer stored on `meta.compat`; consumers derive
 * it from the row's descriptor via `wireTypeOf` (the serialize
 * round-trip uses `typeForRow`).
 */
export function reconcileRowMetadata(
  rawRow: RawRow,
  registries: SheetRegistries,
): NonNullable<RowMetadata["meta"]> {
  return RowTypeSpecOps.reconcileMetadataForWireType(
    rawRow.type,
    rawRow,
    rawRow.meta ?? null,
    registries,
  ) ?? {};
}

function requiredStringAttr(row: RawRow, key: string): string {
  const value = attr(row, key);
  if (typeof value === "string" && value.length > 0) return value;
  throw new Error(
    `RawSheet.toDescriptor: row ${row.id} (${row.type}) requires ` +
      `non-empty string attribute "${key}".`,
  );
}

// ─── Descriptor-builder registry ───────────────────────────────────
// One entry per dispatched row type. Add new row types here; no
// switch-statement edit required.

type DescriptorBuilder = (row: RawRow, registries: SheetRegistries) => Descriptor;

const descriptorBuilders = new Map<string, DescriptorBuilder>([
  // ── Source rows (concrete types) ────────────────────────────────
  //
  // Descriptors carry only type-shape (dtype / cardinality /
  // cellCardinality). Who fills the cells — user, provider, formula
  // — is a runtime property: `FormulaDataSource` dispatch happens on the
  // `RawRow.type === "formula"` wire string; canonical provider
  // rows are identified by a `:~`-prefixed lookupKey matching one
  // of `lona-rows/src/routes.rs`.
  [Source.TEXT, (r) => source(r, { dtype: "text" })],
  // Label rows are display-only banners whose content lives in the
  // row attributes bag. They do not own cell data and render as a
  // block layout row directly through the label plugin.
  [Inert.LABEL, (r) => inert(r, "label")],
  [Source.NUMBER, (r) => source(r, { dtype: "number" })],
  ["checkbox", (r) => source(r, { dtype: "boolean" })],
  [Source.CHECKMARK, (r) => source(r, { dtype: "boolean" })],
  [Source.FORMULA, (r) => source(r, {
    // A formula can emit any dtype — number (the common case),
    // obj (Event / Weather / …), even boolean. Default to number
    // for back-compat with existing fixtures; attrs override.
    dtype: decodedAttr(r, "dtype", DTYPE) ?? "number",
    sourceShape: decodedAttr(r, "cardinality", CARDINALITY) ?? "scalar",
    cellCardinality: cellCardinalityOf(r, "single"),
    ...optionalFormulaSource(decodedAttr(r, "formulaSource", Scalar.string)),
  })],
  [Source.GRAPH, (r) => source(r, { dtype: "number", sourceShape: "per-cell" })],
  [Source.STICKER, (r) => source(r, { dtype: "obj" })],
  [Alias.WEATHER, aliasTemplate("alias.weather")],
  [Alias.TIMEZONE, aliasTemplate("alias.timezone")],
  [Source.SCHEDULING_LINK, (r) => source(r, {
    dtype: "obj",
    cellCardinality: "multi",
  })],
  // Tombstone for the retired pre-alias calendar row type. Keep the wire
  // type recognizable only so old rows render as an explicit Unknown Plugin
  // instead of silently becoming text/data or a partially working calendar.
  ["calendar", retiredRow("calendar")],
  [Alias.CALENDAR, aliasTemplate("alias.calendar")],
  [Alias.CALENDAR_LIST, aliasTemplate("alias.calendar-list")],
  [Alias.TASK, aliasTemplate("alias.task")],
  [Alias.TASK_LIST, aliasTemplate("alias.task-list")],
  [Source.JOURNAL, textSource],
  [Source.DEBUG, textSource],
  // `video` source rows render through the `column.video` renderer
  // (ColumnVideoRendererPlugin) — an embedded YouTube playlist — not as plain text.
  // `dtype: "obj"` carries `objSubtype: "video"` so `wireTypeOf` round-
  // trips the type back to "video" (a text dtype would collapse to "text").
  [Source.VIDEO, (r) =>
    source(r, {
      dtype: "obj",
      rendererOverride: "column.video",
    })],
  ["fallback", textSource],
  ["data-series", objSeriesSource],
  [Inert.SHEET_FOOTER, (r) => inert(r, "sheet-footer")],
  [Inert.TIMELINE_LEGEND, (r) => inert(r, "timeline-legend")],
  // Cell-less provider sync handle (`:~google:{authId}:calendar`). Never authored
  // into a visible sheet, so it isn't rendered in practice — but as a Role::Inert
  // type it carries a builder like every other inert kind, so no parity waiver.
  [Inert.CALENDAR_LIST, (r) => inert(r, "calendar-list")],
  [Source.LOGS, (r) => source(r, { dtype: "obj", sourceShape: "series" })],
  [Source.DATA, dataBuilder],
  ["data-json", dataJsonBuilder],
  [Source.DATA_V1, dataV1Builder],
  // `tasks` — multiple Task objects per cell. Wire type emitted by
  // the server's canonical task row (`:~lona:tasks`).
  // Client-side rendering goes via TASKS_FACADE on top of the
  // DataSource that this descriptor hydrates to.
  [Source.TASKS, (r) => source(r, { dtype: "obj", cellCardinality: "multi" })],
  ["series-summary", (r) => source(r, {
    dtype: "number",
    sourceShape: "series",
  })],
  ["heatmap", (r) => source(r, { dtype: "number", sourceShape: "tensor" })],
  ["calendar-system", (r) => source(r, {
    dtype: "obj",
    rendererOverride: "column.header-secondary",
  })],

  // ── Generic dataSource (short-format placeholder) ───────────────
  // Short-format fixtures use `dataSource [dtype=X, ...]` as a
  // universal source-row placeholder; attrs drive the descriptor.
  [Source.DATA_SOURCE, (r) => source(r, {
    dtype: decodedAttr(r, "dtype", DTYPE) ?? "text",
    sourceShape: decodedAttr(r, "cardinality", CARDINALITY) ?? "scalar",
    cellCardinality: cellCardinalityOf(r, "single"),
  })],
  // ── Derived computed source (Phase-2 DataRef) ──────────────────
  // A `ComputedDataSource` whose cells are derived from its parent via a
  // relative `localPath`. Attrs drive the descriptor exactly like
  // `data-source`; without this entry the row hits the unknown-type fallback
  // (`dtype:"text"`) and renders `column.text` regardless of its attributes.
  ["derived", (r) => source(r, {
    dtype: decodedAttr(r, "dtype", DTYPE) ?? "text",
    sourceShape: decodedAttr(r, "cardinality", CARDINALITY) ?? "scalar",
    cellCardinality: cellCardinalityOf(r, "single"),
  })],
  ["calendar-row", (r) => source(r, {
    dtype: "obj",
    cellCardinality: "multi",
    rendererOverride: "column.event-list",
  })],

  // ── Layout rows ─────────────────────────────────────────────────
  [Layout.COLUMN, (r) => layout(r, "column")],
  [Layout.CANVAS, (r) => layout(r, "canvas")],
  ["data-canvas", (r) => layout(r, "canvas")],
  ["timeline", (r) => layout(r, "timeline[row]")],
  [Layout.TIMELINE_ROW, (r) => layout(r, "timeline[row]")],
  [Alias.CALENDAR_ALLDAY, aliasTemplate("alias.calendar-allday")],
  [Layout.TIMELINE_COLUMN, (r) => layout(r, "timeline[column]")],
  [Layout.GRID, (r) => layout(r, "grid")],
  ["calendar-month", (r) => layout(r, "grid")],
  [Layout.CALENDAR_BODY, (r) => layout(r, "calendar-body")],
  ["list", (r) => source(r, { dtype: "obj", cellCardinality: "multi" })],
  [Inert.GROUP, (r) => inert(r, "group")],
  [Inert.BLOCK, (r) => inert(r, "block")],
  [Inert.HEADER, (r) => inert(r, "header")],

  // ── Surface projection ──────────────────────────────────────────
  // `projection[row]` is a structural relocator: hoists its child
  // wholesale to the surface named in `attributes.surface`. Filtered
  // by `SurfaceProjection.project` before the renderer sees it.
  // `surface` is the synthesized container for one surface
  // (header / body / footer); also synthesized by
  // `SurfaceProjection.project` and accepted here for fixture
  // round-trip and authored cases. Surface, preset, and position
  // live in the row's attribute bag — they do not appear on the
  // descriptor.
  ["projection", (r) => inert(r, "projection")],
  ["projection[row]", (r) => inert(r, "projection")],
  [Inert.SURFACE, (r) => inert(r, "surface")],

  // ── Server-backed surface containers ─────────────────────────────
  // `surface-container` is the wire type for the unique top-level
  // header / footer surface rows of a sheet. Server-stamped
  // `metadata.surface = "header" | "footer"` discriminates which
  // surface; the row itself is intrinsically transparent (no plugin,
  // zero cellHeight on the lona-js descriptor side, skipped from
  // flat-output emission). Its children emit at its offset.
  [Inert.SURFACE_CONTAINER, (r) => inert(r, "surface-container")],

  // ── Test-only synthetic types ───────────────────────────────────
  // `fixture-row` and `fixture-parent` are reserved for childSpec /
  // expansion fixtures (`tests/child-spec-hydrate/`) — obj-dtype
  // sources that don't auto-hydrate a default renderer, so the
  // declared `ROW_TYPE_SPECS.childSpecs` are the
  // only synthesized children.
  ["fixture-row", (r) => source(r, { dtype: "obj" })],
  ["fixture-parent", (r) => source(r, { dtype: "obj" })],

  // ── Renderer rows ───────────────────────────────────────────────
  // LEGACY READ SHIM — the generic `data-renderer` wire type. Qualified
  // renderer ids (`timeline[column].points`, `canvas.bar`, …) are now the
  // canonical `_type` and get identity builders from the catalog loop below
  // this map. This branch is kept only to hydrate rows persisted under the
  // old generic spelling (kind in `attributes.type` / `attributes.rendererId`
  // / `attributes.slot`); it is never emitted for new writes.
  ["data-renderer", (r) => {
    // A materialized default renderer (copy-on-write) persists its full
    // `rendererId`, so any renderer round-trips — not just the canvas/`type`
    // kinds the switch below covers. Prefer it when present.
    const rid = attr(r, "rendererId");
    const persisted = typeof rid === "string" ? rendererIdOf(rid) : null;
    if (persisted != null) {
      return <RendererDescriptor> { role: "renderer", rendererId: persisted };
    }
    const t = attr(r, "type");
    if (t === "timezone") {
      return <RendererDescriptor> {
        role: "renderer",
        rendererId: "column.timezone",
      };
    }
    if (t === "weather") {
      return <RendererDescriptor> {
        role: "renderer",
        rendererId: "column.weather",
      };
    }
    if (t === "logs") {
      return <RendererDescriptor> {
        role: "renderer",
        rendererId: "column.logs",
      };
    }
    if (t === "timeline") {
      return <RendererDescriptor> {
        role: "renderer",
        rendererId: "column.timeline",
      };
    }
    if (t === "timeline-legend") {
      // Shared parent-legend-delegating renderer (ruleset.md R6.1).
      // Slot is required; today only timeline[column] is materialized
      // — body cell is a no-op, parent layout's legend owns the
      // visible surface.
      const slot = attr(r, "slot");
      if (slot !== "timeline[column]") {
        throw new Error(
          `data-renderer type="timeline-legend" requires slot="timeline[column]"; ` +
            `got ${String(slot)}.`,
        );
      }
      return <RendererDescriptor> {
        role: "renderer",
        rendererId: "timeline[column].timeline-legend",
      };
    }
    if (t === "event") {
      const slot = attr(r, "slot");
      if (
        slot === "timeline[row]" || slot === "timeline[column]" ||
        slot === "grid"
      ) {
        return <RendererDescriptor> {
          role: "renderer",
          rendererId: `${slot}.event`,
        };
      }
      // Column slot paints events as a flat vertical list (the
      // architecture table's obj/series/Event → `column.event-list`
      // row), not per-event tiles — hence the id divergence from the
      // `{slot}.event` pattern above.
      if (slot === "column") {
        return <RendererDescriptor> {
          role: "renderer",
          rendererId: "column.event-list",
        };
      }
      throw new Error(
        `data-renderer type="event" requires slot ∈ ` +
          `{"column","timeline[row]","timeline[column]","grid"}; got ${String(slot)}.`,
      );
    }
    if (t === "task") {
      const slot = attr(r, "slot");
      if (slot === undefined || slot === "column") {
        return <RendererDescriptor> {
          role: "renderer",
          rendererId: "column.task",
        };
      }
      if (
        slot === "timeline[row]" || slot === "timeline[column]" ||
        slot === "grid"
      ) {
        return <RendererDescriptor> {
          role: "renderer",
          rendererId: `${slot}.task`,
        };
      }
      throw new Error(
        `data-renderer type="task" requires slot ∈ ` +
          `{"column","timeline[row]","timeline[column]","grid"}; got ${String(slot)}.`,
      );
    }
    if (
      t !== "hrule" &&
      t !== "points" &&
      t !== "line" &&
      t !== "bar" &&
      t !== "candlestick" &&
      t !== "regression-curve" &&
      t !== "regression-band" &&
      t !== "regression-fill"
    ) {
      throw new Error(
        `data-renderer requires a known \`type\` attr; got ${String(t)}.`,
      );
    }
    return <RendererDescriptor> {
      role: "renderer",
      rendererId: `canvas.${t}`,
    };
  }],

  // ── Alias ───────────────────────────────────────────────────────
  [Alias.ALIAS, (r) => <AliasDescriptor> {
    role: "alias",
    template: requiredStringAttr(r, "template"),
  }],
]);

// D-B: one identity builder per catalog renderer-role entry. A
// qualified renderer id is now a legal `_type`; its descriptor is the id
// itself. No prefix dispatch — every id is registered explicitly so a
// typo'd or unregistered id fails loudly through `unknownDescriptorTypes`
// rather than being silently accepted.
for (const entry of ROW_TYPE_CATALOG) {
  if (entry.role !== "renderer") continue;
  const rendererId = rendererIdOf(entry.ty);
  if (rendererId == null) continue;
  descriptorBuilders.set(
    entry.ty,
    () => <RendererDescriptor> { role: "renderer", rendererId },
  );
}

const DESCRIPTOR_BUILDERS: ReadonlyMap<string, DescriptorBuilder> =
  descriptorBuilders;

function aliasTemplate(template: string): DescriptorBuilder {
  return (_row: RawRow) => <AliasDescriptor> {
    role: "alias",
    template,
  };
}

function retiredRow(type: RetiredRendererType): DescriptorBuilder {
  return (row: RawRow) => source(row, { dtype: "obj", rendererOverride: type });
}

function textSource(r: RawRow): SourceDescriptor {
  return source(r, { dtype: "text" });
}

function objSource(r: RawRow): SourceDescriptor {
  return source(r, { dtype: "obj" });
}

function objSeriesSource(r: RawRow): SourceDescriptor {
  return source(r, { dtype: "obj", sourceShape: "series" });
}

const WARNED_UNKNOWN_TYPES = new Set<string>();
function warnUnknownRowType(type: string): void {
  if (WARNED_UNKNOWN_TYPES.has(type)) return;
  WARNED_UNKNOWN_TYPES.add(type);
  // Falls back to a text/scalar source; add an entry to
  // DESCRIPTOR_BUILDERS in this module to type it properly.
  Tracing.log("raw-sheet/unknown-row-type", { type });
}

function dataJsonBuilder(r: RawRow): SourceDescriptor {
  return source(r, { dtype: "obj", sourceShape: "series" });
}

function dataV1Builder(r: RawRow): SourceDescriptor {
  return source(r, { dtype: "obj", sourceShape: "series" });
}

function dataBuilder(r: RawRow, registries: SheetRegistries): Descriptor {
  const lookupKey = r.lookupKey ?? attr(r, "lookupKey");
  if (typeof lookupKey === "string" && registries.dataPresets.get(lookupKey) != null) {
    return {
      role: "alias",
      template: "data",
    };
  }
  return dataJsonBuilder(r);
}

